import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import type { RoutableProps } from "preact-router";
import { revokeReview } from "../api";
import { PrimaryButton } from "../components/buttons";
import { LegalFooter } from "../components/footer";
import { STRINGS } from "../constants/strings";

type Outcome = "idle" | "pending" | "done" | "invalid";

interface Props extends RoutableProps {
  revokeToken?: string;
}

function Page({ children }: { children: ComponentChildren }) {
  return (
    <div class="tl-page">
      <div
        class="tl-page-centered"
        style="padding: 64px var(--tl-page-pad-x) 48px;"
      >
        {children}
      </div>
      <LegalFooter />
    </div>
  );
}

export function RevokeScreen({ revokeToken }: Props) {
  const [outcome, setOutcome] = useState<Outcome>(
    revokeToken ? "idle" : "invalid"
  );
  const [error, setError] = useState<string | null>(null);

  const onRevoke = () => {
    if (!revokeToken) return;
    setError(null);
    setOutcome("pending");
    revokeReview(revokeToken)
      .then((res) => {
        if (res.status === "ok") {
          setOutcome("done");
          return;
        }
        if (res.status === "invalid") {
          setOutcome("invalid");
          return;
        }
        setError(STRINGS.resendRateLimit);
        setOutcome("idle");
      })
      .catch(() => {
        setError(STRINGS.genericError);
        setOutcome("idle");
      });
  };

  if (outcome === "done") {
    return (
      <Page>
        <div class="tl-icon-circle tl-icon-circle--lg">
          <span class="material-icons" aria-hidden="true">
            check_circle
          </span>
        </div>
        <h1 class="tl-screen-title tl-screen-title--xl">
          {STRINGS.revokeSuccessTitle}
        </h1>
        <p class="tl-screen-body">{STRINGS.revokeSuccessBody}</p>
        <p class="tl-screen-body">{STRINGS.revokeSuccessHint}</p>
      </Page>
    );
  }

  if (outcome === "invalid") {
    return (
      <Page>
        <h1 class="tl-screen-title tl-screen-title--xl">
          {STRINGS.revokeInvalidTitle}
        </h1>
        <p class="tl-screen-body">{STRINGS.revokeInvalidBody}</p>
        <p class="tl-screen-body">{STRINGS.revokeInvalidContact}</p>
      </Page>
    );
  }

  return (
    <Page>
      <h1 class="tl-screen-title tl-screen-title--xl">{STRINGS.revokeTitle}</h1>
      <p class="tl-screen-body">{STRINGS.revokeBody}</p>
      {error && (
        <span class="tl-field__error" role="alert">
          {error}
        </span>
      )}
      <PrimaryButton
        text={STRINGS.revokeSubmit}
        loading={outcome === "pending"}
        onClick={onRevoke}
      />
    </Page>
  );
}
